import React, { useMemo } from 'react';
import { Router } from 'react-router';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import MomentUtils from '@date-io/moment';
import { SnackbarProvider } from 'notistack';
import {
  createStyles,
  jssPreset,
  makeStyles,
  StylesProvider,
  ThemeProvider,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import Auth from 'src/components/Auth';
import HubSpotTracking from 'src/components/HubSpotTracking';
import ScrollReset from 'src/components/ScrollReset';
import useSettings from 'src/hooks/useSettings';
import { createTheme } from 'src/theme';
import { THEMES } from 'src/constants';
import Routes from 'src/Routes';
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins] });

const useStyles = makeStyles((theme) => createStyles({
  '@global': {
    '*': {
      boxSizing: 'border-box',
      margin: 0,
      padding: 0,
    },
    html: {
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',
      height: '100%',
      width: '100%',
      // Match theme chrome so overscroll past the footer is not the wrong color.
      backgroundColor: theme.palette.background.dark,
    },
    body: {
      height: '100%',
      width: '100%',
      backgroundColor: theme.palette.background.dark,
    },
    '#root': {
      height: '100%',
      width: '100%',
      backgroundColor: theme.palette.background.dark,
    },
    // Closed keepMounted modals must never intercept scroll/clicks.
    '.MuiModal-root[aria-hidden="true"]': {
      pointerEvents: 'none !important',
    },
  }
}));

function GlobalStyles() {
  useStyles();
  return null;
}

/**
 * Site-wide chrome stays on the original dark theme.
 * Inventory light/dark is applied only inside InventoryThemeProvider (listView).
 * Dark inventory + toggle are gated by INVENTORY_UI_FLAGS.enableDarkTheme.
 */
function ThemedApp() {
  const { settings } = useSettings();
  const { direction, responsiveFontSizes } = settings;
  const theme = useMemo(
    () => createTheme({
      direction,
      responsiveFontSizes,
      theme: THEMES.ONE_DARK,
    }),
    [direction, responsiveFontSizes],
  );

  return (
    <ThemeProvider theme={theme}>
      <StylesProvider jss={jss}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <SnackbarProvider maxSnack={1}>
            <GlobalStyles />
            <Router history={history}>
              <Auth>
                <ScrollReset />
                <HubSpotTracking />
                <Routes />
              </Auth>
            </Router>
          </SnackbarProvider>
        </MuiPickersUtilsProvider>
      </StylesProvider>
    </ThemeProvider>
  );
}

function App() {
  return <ThemedApp />;
}

export default App;
